import { CheckCircle2, Circle, ListTodo, Workflow as WorkflowIcon } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/ui/toast";
import { useNow } from "@/lib/use-now";
import { canMarkTask, dueText, overdue, sortTasks, useLeadTasks, useMarkTask, type Task } from "@/lib/tasks";
import { seesAutomation } from "@/lib/texts";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * Tasks as rows (ACL-408): a round box to mark one done (or open again), the title, when it is due
 * (in red once it is late), and, where the list is not one lead's, the lead it is about, linked
 * when the reader may open it. `showAssignee` names who it is for, on a list of everybody's. The
 * box presses only for the person a task is for, a supervisor or an admin, as core-api allows
 * (`canMarkTask`); for anyone else it says whose task it is.
 */
export function TaskRows({
  tasks, showLead = true, showAssignee = false,
}: {
  tasks: readonly Task[];
  showLead?: boolean;
  showAssignee?: boolean;
}) {
  const now = useNow(30_000);
  const { user } = useAuth();
  // Which workflow made a task is told only to a supervisor and up (ACL-591).
  const sees = seesAutomation(user?.role);
  const mark = useMarkTask();
  const toast = useToast();
  const [busy, setBusy] = React.useState<string | null>(null);
  const flip = (task: Task) => {
    setBusy(task.id);
    mark.mutate(
      { id: task.id, done: task.done_at === null },
      {
        onError: (e) => toast.error("Could not change the task", e instanceof ApiError ? e.message : "Please try again."),
        onSettled: () => setBusy(null),
      },
    );
  };
  return (
    <ul className="divide-y divide-border">
      {sortTasks(tasks).map((task) => {
        const done = task.done_at !== null;
        const late = overdue(task, now);
        const allowed = canMarkTask(task, user);
        return (
          <li key={task.id} className="flex items-start gap-3 py-2.5">
            <button
              type="button"
              onClick={() => flip(task)}
              disabled={busy === task.id || !allowed}
              title={
                allowed
                  ? undefined
                  : `Only ${task.assignee_name ?? "the person it is for"}, a supervisor or an admin can change this task.`
              }
              aria-label={done ? `Open "${task.title}" again` : `Mark "${task.title}" done`}
              aria-pressed={done}
              className="mt-0.5 shrink-0 rounded-full text-text-muted transition-colors hover:text-accent-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40 disabled:opacity-50"
            >
              {done ? <CheckCircle2 className="size-5 text-success-11" /> : <Circle className="size-5" />}
            </button>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className={cn("text-sm font-medium", done ? "text-text-muted line-through" : "text-text")}>{task.title}</p>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-muted">
                <span
                  className={cn(late && "font-medium text-danger-11")}
                  title={done ? `Done ${formatDateTime(task.done_at!)}${task.done_by_name ? ` by ${task.done_by_name}` : ""}` : formatDateTime(task.due_at)}
                >
                  {done ? `Done${task.done_by_name ? ` by ${task.done_by_name}` : ""}` : dueText(task, now)}
                </span>
                {showLead && (
                  <span>
                    {"· "}
                    {task.lead_readable ? (
                      <Link to={`/contacts/${task.lead_id}`} className="font-medium text-text underline-offset-4 hover:underline">
                        {task.lead_name ?? "A contact with no name"}
                      </Link>
                    ) : (
                      <span title="You can't open this contact">{task.lead_name ?? "A contact with no name"}</span>
                    )}
                  </span>
                )}
                {showAssignee && <span>· for {task.assignee_name ?? "nobody"}</span>}
                {task.workflow && sees && (
                  <span className="inline-flex items-center gap-1">
                    · <WorkflowIcon className="size-3" aria-hidden /> {task.workflow.name}
                  </span>
                )}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** A lead's tasks, on its record and in the opportunity dialog. */
export function LeadTasks({ leadId }: { leadId: string }) {
  const tasks = useLeadTasks(leadId);
  if (tasks.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (tasks.error) return <ErrorState error={tasks.error} title="Could not load the tasks" />;
  if (tasks.data.length === 0) {
    return (
      <EmptyState
        icon={ListTodo}
        title="No tasks"
        description="A workflow's Create task step puts a task here, for the person it names, with a due time."
      />
    );
  }
  return <TaskRows tasks={tasks.data} showLead={false} showAssignee />;
}
